import { addCalendarDays, easternMidnightIso } from './timeZones';
import { companyIsoDay } from '../ui/dates';

/**
 * "Is anyone working on this task?" (owner ruling 2026-10-21, after the
 * round-1 Executive persona couldn't tell). Pure: the task's last activity
 * is Tasks.LastActivityAt — server-stamped after every comment or timeline
 * event (api/src/writeRun.ts) — or, on rows from before that column, the
 * row's creation time.
 */

export interface ActivityTask {
  status: 'open' | 'reopened' | 'complete';
  /** YYYY-MM-DD (or longer ISO; the day is the first 10 characters). */
  dueDate: string;
  lastActivityAt?: string | null;
  createdAt?: string | null;
}

const DAY = 86_400_000;

/** When anything last happened on the task (ISO), null when unknown. */
export function lastActivityOf(task: ActivityTask): string | null {
  for (const v of [task.lastActivityAt, task.createdAt]) {
    if (v && Number.isFinite(Date.parse(v))) return v;
  }
  return null;
}

/** Whole company-calendar (Eastern) days from `iso` to `now` (0 = today); null when unknown. */
export function daysSince(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const [a, b] = [companyIsoDay(then), companyIsoDay(now)].map((d) => {
    const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, dd);
  }) as [number, number];
  return Math.max(0, Math.round((b - a) / DAY));
}

const ago = (days: number) => (days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`);

/**
 * "Last activity today" / "… yesterday" / "… 5 days ago". A task nothing
 * has happened on yet reads "Created 7 days ago — no activity yet" (exec
 * round 3 F5: "Last activity 7 days ago" beside "No activity yet" read
 * oddly — the 7 days was the creation): no LastActivityAt stamp, or
 * `noEntries` (the task page knows its thread is empty).
 */
export function activityLabel(task: ActivityTask, now: Date = new Date(), opts: { noEntries?: boolean } = {}): string {
  const days = daysSince(lastActivityOf(task), now);
  if (days === null) return 'No activity yet';
  const stamped = Boolean(task.lastActivityAt && Number.isFinite(Date.parse(task.lastActivityAt)));
  if (opts.noEntries || !stamped) return `Created ${ago(days)} — no activity yet`;
  return `Last activity ${ago(days)}`;
}

/**
 * An open task that is OVERDUE and has had no activity since it fell
 * overdue — nothing since the start of the day after its due date (Eastern
 * midnight — the company calendar, 2026-10-28).
 * Unknown activity counts as untouched.
 */
export function isUntouchedOverdue(task: ActivityTask, now: Date = new Date()): boolean {
  if (task.status === 'complete' || !task.dueDate) return false;
  const due = task.dueDate.slice(0, 10);
  if (!(due < companyIsoDay(now))) return false; // not overdue (same rule as isOverdue)
  const overdueFrom = Date.parse(easternMidnightIso(addCalendarDays(due, 1))); // Eastern midnight after the due day
  // Only real activity counts after the due date: a task CREATED already
  // overdue (an ad-hoc task with a past date) is untouched until someone acts.
  const stamped = task.lastActivityAt && Number.isFinite(Date.parse(task.lastActivityAt)) ? Date.parse(task.lastActivityAt) : null;
  const created = task.createdAt && Number.isFinite(Date.parse(task.createdAt)) ? Date.parse(task.createdAt) : null;
  const last = stamped ?? (created !== null && created < overdueFrom ? created : null);
  return last === null || last < overdueFrom;
}
